import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import {
    createBrowserRouter, Navigate, Outlet, RouteObject, RouterProvider
} from 'react-router-dom';

import { FeatureFlags } from './configs/FeatureFlags';
import { AuthenticatedRoute } from './components/Routing/AuthenticatedRoute';
import { AuthContext } from './context/AuthContext';
import Store from './redux-store/Store';
import { CategoryScreen } from './screens/dashboard/Category';
import { ComplianceScreen } from './screens/dashboard/Compliance';
import { DashboardScreen } from './screens/dashboard/Dashboard';
import { ExploreScreen } from './screens/dashboard/explore/Explore';
import { ExploreSurveyGoals } from './screens/dashboard/explore/ExploreSurveyGoals';
import { ExploreSurveyShell } from './screens/dashboard/explore/ExploreSurveyShell';
import { EXPLORE_SURVEY_DOMAINS } from './configs/ExploreSurveyConfig';
import { Incentives } from './screens/dashboard/Incentives/Incentives';
import { IncentiveDetail } from './screens/dashboard/Incentives/IncentiveDetail';
import { DataMiner } from './screens/dashboard/Incentives/DataMiner';
import { DataMinerCategory } from './screens/dashboard/Incentives/DataMinerCategory';
import { PortfolioScreen } from './screens/dashboard/Portfolio';
import { Screening } from './screens/dashboard/Screening';
import { ScreeningSandbox } from './screens/dashboard/ScreeningSandbox';
import { DemoStartScreen } from './screens/demo/DemoStart';
import { DerapiTestScreen } from './screens/demo/DerapiTest';
import { Login } from './screens/Login';
import { AccountPasswordScreen } from './screens/onboarding/account/AccountPassword';
import { AccountSignupScreen } from './screens/onboarding/account/AccountSignup';
import { AccountVerificationScreen } from './screens/onboarding/account/AccountVerification';
import { CompanyScreen } from './screens/onboarding/company/Company';
import { PropertyScreen } from './screens/onboarding/property/Property';
import { OnboardingSurveyScreen } from './screens/onboarding/survey/OnboardingSurveyScreen';
import { SurveysHubScreen } from './screens/onboarding/surveys/SurveysHub';
import { SummaryScreen } from './screens/onboarding/summary/Summary';
import { UapiConfirmationScreen } from './screens/onboarding/utility-link/UapiConfirmation';
import { UapiManualScreen } from './screens/onboarding/utility-link/UapiManual';
import { UtilityLinkScreen } from './screens/onboarding/utility-link/UtilityLink';
import { ResetPassword } from './screens/ResetPassword';
import { ProjectStartScreen } from './screens/projects/ProjectStart';
import { ProjectDetailScreen } from './screens/projects/ProjectDetail';
import { ProjectsListScreen } from './screens/projects/ProjectsList';
import { ProfileScreen } from './screens/account/Profile';
import { SettingsScreen } from './screens/account/Settings';
import { AccountActivationScreen } from './screens/account/AccountActivation';
import { SuperAdminOptInScreen } from './screens/account/SuperAdminOptIn';
import { UapiCallbackScreen } from './screens/utility-link/UapiCallbackScreen';
import { Analytics } from './services/analytics/Analytics';
import { ONBOARDING_BASE_PATH } from './services/nav/Constants';
import { IZeosUserContract } from './services/zeos-api/contracts/Account';

export const App = () => {
  const authContext = useContext(AuthContext);

  const [loggedIn, setLoggedIn] = useState(false);
  const [authResolved, setAuthResolved] = useState(false);

  useEffect(() => {
    const unsubscribe = authContext.observeAuth(
      async (user: IZeosUserContract) => {
        const isLoggedIn = user != null;
        // observeAuth hands back the Firebase User despite the contract typing.
        const firebaseUid = (user as unknown as { uid?: string } | null)?.uid;
        if (firebaseUid) {
          Analytics.identify(firebaseUid);
        } else {
          Analytics.reset();
        }
        setLoggedIn(isLoggedIn);
        setAuthResolved(true);
      }
    );
    return () => {
      try {
        // Firebase returns an unsubscribe function
        typeof unsubscribe === "function" && unsubscribe();
      } catch {}
    };
  }, [authContext]);

  const AppLayout = () => {
    return <Outlet />;
  };

  const withAuthGuard = useMemo(
    () => (element: React.ReactNode) =>
      (
        <AuthenticatedRoute loggedIn={loggedIn} authResolved={authResolved}>
          {element}
        </AuthenticatedRoute>
      ),
    [authResolved, loggedIn]
  );

  const complianceSlug = "compliance";
  const dashboardSlug = "dashboard";
  const exploreSlug = "explore";
  const incentivesSlug = "incentives";
  const portfolioSlug = "portfolio";
  const projectsSlug = "projects";
  const screeningSlug = "screening";

  const onboardRoutes: RouteObject[] = [
    {
      path: ONBOARDING_BASE_PATH,
      children: [
        { path: "", element: <AccountSignupScreen /> },
        {
          path: "demo",
          element: <DemoStartScreen />,
        },
        {
          path: "account-setup",
          element: <AccountSignupScreen />,
        },
        {
          path: "account-verification",
          element: <AccountVerificationScreen />,
        },
        {
          path: "account-password",
          element: <AccountPasswordScreen />,
        },
        {
          path: "company",
          element: <CompanyScreen />,
        },
        {
          path: "survey/:domainSlug",
          element: <OnboardingSurveyScreen />,
        },
        {
          path: "surveys",
          element: <SurveysHubScreen />,
        },
        {
          path: "property",
          element: <PropertyScreen />,
        },
        {
          path: "summary",
          element: <SummaryScreen />,
        },
        {
          path: "uapi",
          element: <UtilityLinkScreen />,
        },
        {
          path: "uapi-confirm",
          element: <UapiConfirmationScreen />,
        },
        {
          path: "uapi-manual",
          element: <UapiManualScreen />,
        },
      ],
    },
  ];

  const appRoutes: RouteObject[] = [
    ...onboardRoutes,
    {
      path: "/",
      element: loggedIn ? (
        withAuthGuard(<Navigate to={`/${portfolioSlug}`} replace />)
      ) : (
        <Navigate to="/login" replace />
      ),
    },
    {
      path: "/login",
      element: !authResolved ? (
        <div className="flex items-center justify-center h-screen">
          <p>Loading...</p>
        </div>
      ) : loggedIn ? (
        <Navigate to={`/${portfolioSlug}`} replace />
      ) : (
        <Login />
      ),
    },
    {
      path: "/reset-password",
      element: <ResetPassword />,
    },
    {
      // Admin-created users activate here rather than in the onboarding tree: they
      // already have a company, so onboarding has nothing to ask them.
      path: "/activate",
      element: <AccountActivationScreen />,
    },
    {
      path: "/super-admin/opt-in",
      element: <SuperAdminOptInScreen />,
    },
    {
      path: "/utility-link-complete",
      element: withAuthGuard(<UapiCallbackScreen />),
    },
    {
      path: portfolioSlug,
      element: withAuthGuard(<PortfolioScreen />),
    },
    {
      path: incentivesSlug,
      element: withAuthGuard(<Incentives />),
    },
    ...(FeatureFlags.enableServiceProviderScreeningPilot
      ? [
          {
            path: screeningSlug,
            element: withAuthGuard(<Screening />),
          },
        ]
      : []),
    {
      path: "screening-sandbox",
      element: withAuthGuard(<ScreeningSandbox />),
    },
    {
      path: `${incentivesSlug}/data-miner`,
      element: withAuthGuard(<DataMiner />),
    },
    {
      path: `${incentivesSlug}/data-miner/:category`,
      element: withAuthGuard(<DataMinerCategory />),
    },
    {
      path: `${incentivesSlug}/:incentiveId`,
      element: withAuthGuard(<IncentiveDetail />),
    },
    {
      path: projectsSlug,
      element: withAuthGuard(<ProjectsListScreen />),
    },
    {
      path: "projects/start",
      element: withAuthGuard(<ProjectStartScreen />),
    },
    {
      path: "projects/:projectId",
      element: withAuthGuard(<ProjectDetailScreen />),
    },
    {
      path: "/profile",
      element: withAuthGuard(<ProfileScreen />),
    },
    {
      path: "/settings",
      element: withAuthGuard(<SettingsScreen />),
    },
    {
      path: `${dashboardSlug}`,
      element: withAuthGuard(<DashboardScreen />),
    },
    {
      path: `${dashboardSlug}`,
      element: withAuthGuard(<AppLayout />),
      children: [
        {
          path: ":category",
          element: <CategoryScreen />,
        },
      ],
    },
    {
      path: exploreSlug,
      element: withAuthGuard(<ExploreScreen />),
    },
    {
      path: complianceSlug,
      element: withAuthGuard(<ComplianceScreen />),
    },
    ...EXPLORE_SURVEY_DOMAINS.flatMap((domain) => [
      {
        path: `${exploreSlug}/${domain.slug}/intent`,
        element: withAuthGuard(
          <ExploreSurveyShell key={domain.slug} domainConfig={domain} />
        ),
      },
      {
        path: `${exploreSlug}/${domain.slug}/goals`,
        element: withAuthGuard(
          <ExploreSurveyGoals key={domain.slug} domainConfig={domain} />
        ),
      },
    ]),
    {
      path: "demo/derapi",
      element: withAuthGuard(<DerapiTestScreen />),
    },
    {
      path: "/demo/derapi",
      element: withAuthGuard(<DerapiTestScreen />),
    },

    {
      path: "*",
      element: <Navigate to="/" replace />,
    },
  ];

  const appRouter = createBrowserRouter(appRoutes, {
    basename: import.meta.env.BASE_URL,
  });

  return (
    <Provider store={Store}>
      <div className="h-screen">
        <RouterProvider router={appRouter} />
      </div>
    </Provider>
  );
};
